import React, { useContext } from "react";
import { flexRender } from "@tanstack/react-table";
import styled, { css } from "styled-components";
import { Table as BaseTable } from "components/bootstrap";
import EntityTableOverrideRow from "components/common/EntityDataTable/EntityTableOverrideRow";
import ExpandedSections from "components/common/EntityDataTable/ExpandedSections";
import { ACTIONS_COL_ID, CELL_PADDING } from "components/common/EntityDataTable/Constants";
import {
  columnOpacityVar,
  columnTransformVar,
  columnTransition,
  displayScrollRightIndicatorVar
} from "components/common/EntityDataTable/CSSVariables";
import ScrollShadow from "theme/box-shadows/ScrollShadow";
import ExpandedEntitiesSectionsContext from "./contexts/ExpandedSectionsContext";
import TableHead from "./TableHead";
const StyledTable = styled(BaseTable)(
  ({ theme }) => css`
    table-layout: fixed;
    margin-bottom: 0;
    height: 100%; // required to be able to use height: 100% in td

    thead > tr > th,
    tbody > tr > td {
      padding: ${CELL_PADDING}px;
    }

    && {
      > tbody:nth-of-type(even) > tr {
        background-color: ${theme.colors.table.row.backgroundStriped};
      }

      > tbody:nth-of-type(odd) > tr {
        background-color: ${theme.colors.table.row.background};
      }
      > tbody > tr.active {
        background-color: ${theme.colors.table.row.backgroundStriped} !important;

        & td {
          background-color: transparent !important;
        }
      }
    }
  `
);
const Td = styled.td(
  ({ $colId, $hidePadding, $pinningPosition }) => css`
    word-break: break-word;
    opacity: var(${columnOpacityVar($colId)}, 1);
    transform: var(${columnTransformVar($colId)}, none);
    transition: var(${columnTransition()}, none);
    height: 100%; // required to be able to use height: 100% in child elements
    ${$pinningPosition ? css`
          position: sticky;
          ${$pinningPosition === "left" ? "left" : "right"}: 0;
          ${ScrollShadow("left")}
          &::before {
            display: var(${displayScrollRightIndicatorVar}, none);
          }
        ` : ""}

    ${$hidePadding && css`
      && {
        padding: 0;
      }
    `}
  `
);
const Table = ({
  expandedSectionRenderers,
  rowOverride = void 0,
  headerGroups,
  rows
}) => {
  const { expandedSections } = useContext(ExpandedEntitiesSectionsContext);
  const isRowExpanded = (rowId) => !!(expandedSections == null ? void 0 : expandedSections[rowId]);
  return /* @__PURE__ */ React.createElement(StyledTable, { striped: true, condensed: true, hover: true }, /* @__PURE__ */ React.createElement(TableHead, { headerGroups }), rows.map((row) => {
    const visibleCells = row.getVisibleCells();
    const visibleCellCount = visibleCells.length;
    const renderCell = (cell) => {
      const columnMeta = cell.column.columnDef.meta;
      return /* @__PURE__ */ React.createElement(
        Td,
        {
          key: cell.id,
          $colId: cell.column.id,
          $pinningPosition: cell.column.getIsPinned(),
          $hidePadding: columnMeta == null ? void 0 : columnMeta.hideCellPadding
        },
        flexRender(cell.column.columnDef.cell, cell.getContext())
      );
    };
    const actionCellIndex = visibleCells.findIndex((cell) => cell.column.id === ACTIONS_COL_ID);
    const defaultRowActionCell = actionCellIndex >= 0 ? renderCell(visibleCells[actionCellIndex]) : void 0;
    const overrideNotice = rowOverride == null ? void 0 : rowOverride(row.original);
    return /* @__PURE__ */ React.createElement("tbody", { key: `table-row-${row.id}`, "data-testid": `table-row-${row.id}` }, overrideNotice !== void 0 ? /* @__PURE__ */ React.createElement(
      EntityTableOverrideRow,
      {
        visibleCellCount,
        notice: overrideNotice,
        actionCell: defaultRowActionCell
      }
    ) : /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("tr", { className: isRowExpanded(row.id) ? "active" : null }, visibleCells.map(renderCell)), /* @__PURE__ */ React.createElement(
      ExpandedSections,
      {
        key: `expanded-sections-${row.id}`,
        expandedSectionRenderers,
        entity: row.original
      }
    )));
  }));
};
export default React.memo(Table);
